import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Empty, Input, notification, Spin, Tabs} from 'antd';
import {
    BarcodeOutlined,
    FullscreenExitOutlined,
    FullscreenOutlined,
    SearchOutlined,
    UserOutlined
} from '@ant-design/icons';
import {posActions, posActiveTabDataSelector, posActiveTabSelector, posTabsSelector} from '~/reduxs/Pos/posSlice';
import {currentUserSelector} from '~/reduxs/Auth/authSlice';
import {Button, Icon, Image, Loading} from '~/components';
import ProductItem from '../components/ProductItem';
import CartItem from '../components/CartItem';
import SuggestMoney from '../components/SuggestMoney';
import BillPrint from '~/components/BillPrint';
import SyncStatus from '../components/SyncStatus';
import AddCustomerModal from '../components/AddCustomerModal';
import {apiError, formatCurrency, handleRequest, numberFormat, numberToWords} from '~/utils';
import {customerApi, orderApi, utilsApi} from '~/api';
import {useReactToPrint} from 'react-to-print';
import {useLocalProducts} from '~/hooks';
import indexedDBService from '~/services/indexedDBService';
import {AppContext} from "../../../context/AppProvider";
import '../style/Pos.scss';
import {FontAwesomeIcon} from "../../../components";

const {TabPane} = Tabs;

const Pos = () =>
{
    const {appData} = useContext(AppContext);

    const dispatch = useDispatch();

    const tabs = useSelector(posTabsSelector);
    const activeTabId = useSelector(posActiveTabSelector);
    const activeTab = useSelector(posActiveTabDataSelector);

    const currentUser = useSelector(currentUserSelector);

    // Local products hook
    const { searchProducts, syncProducts, isReady: localProductsReady } = useLocalProducts();

    const [customerSearch, setCustomerSearch] = useState('');
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [productSearch, setProductSearch] = useState('');
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [discount, setDiscount] = useState(0);
    const [discountType, setDiscountType] = useState('amount');
    const [shipping, setShipping] = useState(0);
    const [shippingDisplay, setShippingDisplay] = useState('0');
    const [paid, setPaid] = useState(0);
    const [paidDisplay, setPaidDisplay] = useState('0'); // Formatted display value
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [barcodeMode, setBarcodeMode] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [showProducts, setShowProducts] = useState(false);
    const [mobileCartTab, setMobileCartTab] = useState('items'); // 'items' or 'checkout'
    const [qrLoading, setQrLoading] = useState(false);
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [lastCreatedOrder, setLastCreatedOrder] = useState(null);

    const searchTimeoutRef = useRef(null);
    const customerSearchTimeoutRef = useRef(null);
    const billPrintRef = useRef(null);
    const orderDataRef = useRef(null); // Ref to store order data for printing

    // Load products from IndexedDB
    const loadProducts = useCallback(async (keyword = '') =>
    {
        if (!localProductsReady)
        {
            return;
        }

        setProductsLoading(true);

        try
        {
            let results;

            if (keyword && keyword.trim() !== '') {
                results = await searchProducts(keyword, 100);
            }
            else
            {
                // Load all products or top 100
                results = await searchProducts('', 100);
            }

            setProducts(results);
        }
        catch (error)
        {
            notification.error({
                message: 'Lỗi',
                description: 'Không thể tải danh sách sản phẩm'
            });
        }
        finally
        {
            setProductsLoading(false);
        }
    }, [searchProducts, localProductsReady]);

    // Load products on mount
    useEffect(() => {
        if (localProductsReady) {
            loadProducts();
        }
    }, [localProductsReady, loadProducts]);

    // Auto sync products every time POS page is loaded
    useEffect(() => {
        if (localProductsReady) {
            syncProducts().then(() => {
                // Reload products after sync completes to get latest data
                loadProducts(productSearch);
            });
        }
    }, [localProductsReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load offline invoice on mount
    useEffect(() => {
        const loadOfflineInvoice = async () => {
            try {
                const offlineData = await indexedDBService.getOfflineInvoice();

                if (offlineData && offlineData.tabs && offlineData.tabs.length > 0) {
                    // Check if offline data has actual content
                    const hasContent = offlineData.tabs.some(tab =>
                        tab.items.length > 0 ||
                        tab.customer !== null ||
                        tab.discount > 0 ||
                        tab.paid > 0
                    );

                    if (hasContent) {
                        console.log('📋 Restoring offline invoice...', offlineData);
                        dispatch(posActions.restoreState({
                            tabs: offlineData.tabs,
                            activeTab: offlineData.activeTab,
                            nextTabId: offlineData.nextTabId,
                        }));

                        notification.success({
                            message: 'Khôi phục hóa đơn',
                            description: 'Đã khôi phục hóa đơn chưa hoàn thành',
                            duration: 3,
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to load offline invoice:', error);
            }
        };

        loadOfflineInvoice();
    }, [dispatch]);

    // Search customers function
    const searchCustomers = useCallback(async (keyword) => {
        if (!keyword || keyword.trim() === '') {
            setCustomers([]);
            setShowCustomerDropdown(false);
            return;
        }

        setCustomersLoading(true);

        try {
            let [error, response] = await handleRequest(
                customerApi.search({ keyword, limit: 50 })
            );

            if (!apiError('Tìm kiếm khách hàng thất bại', error, response)) {
                setCustomers(response.data || []);
                setShowCustomerDropdown(true);
            }
        }
        catch (err)
        {
            console.error('Error searching customers:', err);
        }
        finally
        {
            setCustomersLoading(false);
        }
    }, []);

    // Debounced customer search effect
    useEffect(() => {
        if (customerSearchTimeoutRef.current) {
            clearTimeout(customerSearchTimeoutRef.current);
        }

        customerSearchTimeoutRef.current = setTimeout(() => {
            searchCustomers(customerSearch);
        }, 300);

        return () => {
            if (customerSearchTimeoutRef.current) {
                clearTimeout(customerSearchTimeoutRef.current);
            }
        };
    }, [customerSearch, searchCustomers]);

    // Debounced search effect
    useEffect(() => {
        // Clear timeout nếu có
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set timeout mới để debounce
        searchTimeoutRef.current = setTimeout(() => {
            loadProducts(productSearch);
        }, 300);

        // Cleanup function
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [productSearch, loadProducts]);

    // Sync discount and paid with active tab
    useEffect(() => {
        if (activeTab) {
            setDiscount(activeTab.discount);
            setDiscountType(activeTab.discountType);
            setPaid(activeTab.paid);
            setPaidDisplay(numberFormat(activeTab.paid, 0));
        }
    }, [activeTab]);

    // Auto-update paid amount when total changes (only if not manually set)
    useEffect(() => {
        if (activeTab && !activeTab.isPaidManuallySet && activeTab.items.length > 0) {
            // Calculate total inline using the helper function
            const subtotal = activeTab.items.reduce((sum, item) => {
                return sum + calculateItemSubtotal(item);
            }, 0);

            const totalVat = activeTab.items.reduce((sum, item) => {
                const itemSubtotal = calculateItemSubtotal(item);
                return sum + (itemSubtotal * (item.vat || 0) / 100);
            }, 0);

            const totalBeforeDiscount = subtotal + totalVat;

            const currentDiscount = activeTab.discount || 0;
            const currentDiscountType = activeTab.discountType || 'amount';

            const discountAmount = currentDiscountType === 'percent'
                ? (totalBeforeDiscount * currentDiscount / 100)
                : currentDiscount;

            const total = totalBeforeDiscount - discountAmount;

            // Only update if different
            if (Math.abs(total - activeTab.paid) > 0.01) {
                setPaid(total);
                setPaidDisplay(numberFormat(total, 0));
                dispatch(posActions.setPaid({
                    tabId: activeTabId,
                    paid: total,
                    isManual: false // Auto-update, not manual
                }));
            }
        }
    }, [activeTab?.items, activeTab?.discount, activeTab?.discountType, activeTab?.isPaidManuallySet, activeTab?.paid, activeTabId, dispatch]);

    const handleAddTab = () => {
        dispatch(posActions.addTab());
    };

    const handleRemoveTab = (tabId) => {
        if (tabs.length > 1) {
            dispatch(posActions.removeTab(tabId));
        }
    };

    const handleTabChange = (tabId) => {
        dispatch(posActions.setActiveTab(parseInt(tabId)));
        setLastCreatedOrder(null); // Clear last order when switching tabs
        orderDataRef.current = null;
    };

    const handleAddProduct = (product) => {
        // Clear last order data when starting a new order
        if (activeTab?.items?.length === 0 && lastCreatedOrder) {
            setLastCreatedOrder(null);
            orderDataRef.current = null;
        }

        // Set initial quantity: 0.1 for kg products, 1 for others
        const initialQuantity = product.unit === 'kg' ? 0.1 : 1;

        dispatch(posActions.addItem({
            tabId: activeTabId,
            item: {
                ...product,
                quantity: initialQuantity
            }
        }));

        // Hide products panel on mobile after adding product
        setShowProducts(false);
    };

    const handleIncreaseQuantity = (itemId) => {
        const item = activeTab.items.find(i => i.id === itemId);
        if (item) {
            const step = item.unit === 'kg' ? 0.1 : 1;
            const newQuantity = item.unit === 'kg'
                ? Math.round((item.quantity + step) * 10) / 10
                : item.quantity + step;
            dispatch(posActions.updateItemQuantity({
                tabId: activeTabId,
                itemId,
                quantity: newQuantity
            }));
        }
    };

    const handleDecreaseQuantity = (itemId) => {
        const item = activeTab.items.find(i => i.id === itemId);
        if (item && item.quantity > 0) {
            const step = item.unit === 'kg' ? 0.1 : 1;
            const newQuantity = item.unit === 'kg'
                ? Math.max(0, Math.round((item.quantity - step) * 10) / 10)
                : Math.max(0, item.quantity - step);
            dispatch(posActions.updateItemQuantity({
                tabId: activeTabId,
                itemId,
                quantity: newQuantity
            }));
        }
    };

    const handleQuantityChange = (itemId, quantity) => {
        if (quantity >= 0) {
            dispatch(posActions.updateItemQuantity({
                tabId: activeTabId,
                itemId,
                quantity
            }));
        }
    };

    const handlePriceChange = (itemId, price) => {
        if (price >= 0) {
            dispatch(posActions.updateItemPrice({
                tabId: activeTabId,
                itemId,
                price
            }));
        }
    };

    const handleRemoveItem = (itemId) => {
        dispatch(posActions.removeItem({
            tabId: activeTabId,
            itemId
        }));
    };

    const handleDiscountChange = (value) => {
        setDiscount(value);
        dispatch(posActions.setDiscount({
            tabId: activeTabId,
            discount: value,
            discountType
        }));
    };

    const handleDiscountTypeChange = (type) => {
        setDiscountType(type);
        dispatch(posActions.setDiscount({
            tabId: activeTabId,
            discount,
            discountType: type
        }));
    };

    const handleShippingChange = (value) => {
        console.log(value)
        const numericValue = parseFloat(value.toString().replace(/[^\d.]/g, '')) || 0;
        console.log(numericValue)
        setShipping(numericValue);
        setShippingDisplay(numberFormat(numericValue, 0));
        dispatch(posActions.setShipping({
            tabId: activeTabId,
            shipping: numericValue,
        }));
    };

    const handlePaidChange = (value) => {
        // Remove all non-numeric characters except decimal point
        const numericValue = parseFloat(value.toString().replace(/[^\d.]/g, '')) || 0;
        setPaid(numericValue);
        setPaidDisplay(numberFormat(numericValue, 0));
        dispatch(posActions.setPaid({
            tabId: activeTabId,
            paid: numericValue,
            isManual: true // User manually changed the value
        }));
    };

    const handlePaymentMethodChange = async (method) => {
        dispatch(posActions.setPaymentMethod({
            tabId: activeTabId,
            paymentMethod: method
        }));

        if (method === 'transfer') {
            // Load QR code when switching to transfer
            await loadQrCode();
        } else {
            // Clear QR code when switching to cash
            dispatch(posActions.setQrCodeUrl({
                tabId: activeTabId,
                qrCodeUrl: ''
            }));
        }
    };

    const handleOpenAddCustomer = () => {
        setShowAddCustomerModal(true);
    };

    const handleCloseAddCustomer = () => {
        setShowAddCustomerModal(false);
    };

    const handleAddCustomerSuccess = (customer) => {
        // Add customer to active tab
        dispatch(posActions.setCustomer({
            tabId: activeTabId,
            customer: {
                id: customer.id,
                code: customer.code,
                name: customer.name,
                phone: customer.phone,
                address: customer.address,
            }
        }));

        notification.success({
            message: 'Thành công',
            description: `Đã thêm khách hàng: ${customer.name}`
        });
    };

    const handleClearCustomer = () => {
        dispatch(posActions.setCustomer({
            tabId: activeTabId,
            customer: null
        }));
        setCustomerSearch('');
    };

    const handleSelectCustomer = (customer) => {
        dispatch(posActions.setCustomer({
            tabId: activeTabId,
            customer: {
                id: customer.id,
                code: customer.code,
                name: customer.name,
                phone: customer.phone,
                address: customer.address,
            }
        }));
        setCustomerSearch('');
        setShowCustomerDropdown(false);
        setCustomers([]);
    };

    const loadQrCode = async () => {
        if (!activeTab || activeTab.items.length === 0) {
            return;
        }

        const totals = calculateTotals();

        setQrLoading(true);
        let [error, response] = await handleRequest(
            utilsApi.qrPayment({
                amount: totals.total,
                description: `Thanh toán đơn hàng`
            })
        );
        setQrLoading(false);

        if (!apiError('Tạo mã QR thất bại', error, response)) {
            const qrUrl = response.data?.qr_url || '';
            // Dispatch vào Redux
            dispatch(posActions.setQrCodeUrl({
                tabId: activeTabId,
                qrCodeUrl: qrUrl
            }));
        }
    };

    const handleCheckout = async () => {
        // Validate
        if (!activeTab || activeTab.items.length === 0)
        {
            notification.error({
                message: 'Lỗi',
                description: 'Vui lòng thêm sản phẩm vào giỏ hàng'
            });
            return;
        }

        if (paid < totals.total) {
            notification.error({
                message: 'Lỗi',
                description: 'Số tiền khách đưa không đủ'
            });
            return;
        }

        await processCheckout();
    };

    const processCheckout = async () => {

        setCheckoutLoading(true);

        // Prepare order data
        const orderData = {
            customer_id: activeTab.customer?.id || null,
            customer_name: activeTab.customer?.name || '',
            customer_phone: activeTab.customer?.phone || '',
            customer_address: activeTab.customer?.address || '',
            items: activeTab.items.map(item => ({
                id: item.id,
                title: item.title,
                code: item.code,
                price: item.price,
                quantity: item.quantity,
                unit: item.unit,
                vat: item.vat || 0,
                attribute_label: item.attribute_label || '',
            })),
            subtotal: totals.subtotal,
            total_vat: totals.totalVat,
            discount: discount,
            discount_type: discountType,
            discount_amount: totals.discountAmount,
            shipping: totals.shipping,
            total: totals.total,
            paid: paid,
            change: totals.change,
            payment_method: activeTab?.paymentMethod || 'cash',
            note: '',
        };

        let [error, response] = await handleRequest(orderApi.add(orderData));

        setCheckoutLoading(false);

        if (!apiError('Tạo đơn hàng thất bại', error, response)) {
            // Lưu thông tin order từ server vào ref (đồng bộ)
            const orderData = response.data;
            orderDataRef.current = orderData;
            setLastCreatedOrder(orderData); // Cũng lưu vào state để có thể in lại sau

            notification.success({
                message: 'Thành công',
                description: 'Thanh toán thành công!'
            });

            // Delete offline invoice after successful checkout
            indexedDBService.deleteOfflineInvoice().catch(err => {
                console.error('Failed to delete offline invoice:', err);
            });

            // Send invoice data to mobile app for thermal printing
            if (window.ReactNativeWebView)
            {
                try {
                    window.ReactNativeWebView.postMessage(JSON.stringify({
                        action: 'printInvoice',
                        data: {
                            storeName: 'GIỎ CHÁ TUYẾT',
                            storeAddress: 'Địa chỉ cửa hàng của bạn',
                            invoiceNumber: orderData.code || activeTab?.code || 'N/A',
                            date: new Date().toLocaleString('vi-VN'),
                            items: activeTab.items.map(item => ({
                                name: item.product?.name || 'Sản phẩm',
                                quantity: item.quantity || 0,
                                price: item.product?.sell_price || 0,
                                total: (item.quantity || 0) * (item.product?.sell_price || 0)
                            })),
                            subtotal: totals.subtotal || 0,
                            discount: discount || 0,
                            total: totals.total || 0,
                            paid: paid || 0,
                            change: totals.change || 0,
                            footer: 'Cảm ơn quý khách! Hẹn gặp lại!'
                        }
                    }));
                }
                catch (err)
                {
                    console.error('Error sending invoice to mobile app:', err);
                }
            }

            // Clear cart BEFORE printing
            handleClearCart();

            // Auto print bill after checkout with server data
            // Increase delay to allow state updates and DOM to settle
            setTimeout(() => {
                handlePrint();
            }, 500); // Increased from 200ms to 500ms
        }
    };

    // Detect mobile device
    const isMobileDevice = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
            || window.innerWidth <= 768;
    };

    // Simple print handler using window.print - works for both mobile and desktop
    const handleSimplePrint = useCallback(() =>
    {
        if (!billPrintRef.current)
        {
            notification.error({
                message: 'Lỗi in',
                description: 'Không tìm thấy hóa đơn để in.',
                duration: 3
            });
            return;
        }

        // Debug: Check if we have bill data
        const billData = {
            code: orderDataRef.current?.code || lastCreatedOrder?.code || activeTab?.code || 'HD001',
            items: orderDataRef.current?.items || lastCreatedOrder?.items || activeTab?.items || []
        };

        if (billData.items.length === 0) {
            notification.warning({
                message: 'Không có dữ liệu',
                description: 'Không có sản phẩm nào để in hóa đơn.',
                duration: 3
            });
            return;
        }

        const billElement = billPrintRef.current;

        // Get all parent elements up to body
        const parents = [];

        let currentElement = billElement.parentElement;

        while (currentElement && currentElement !== document.body)
        {
            parents.push(currentElement);
            currentElement = currentElement.parentElement;
        }

        // Add print-mode class to body
        document.body.classList.add('print-mode');

        // Add print-active class to bill parent
        if (parents[0])
        {
            parents[0].classList.add('bill-print-parent');
        }

        // Save original styles
        const originalStyles = parents.map(el => ({
            element: el,
            display: el.style.display,
            visibility: el.style.visibility
        }));

        // Make all parents visible
        parents.forEach(parent => {
            parent.style.display = 'block';
            parent.style.visibility = 'visible';
        });

        // Also ensure bill element itself is visible
        const originalBillDisplay = billElement.style.display;
        billElement.style.display = 'block';
        billElement.style.visibility = 'visible';

        // Restore function
        const restore = () => {

            // Remove classes
            document.body.classList.remove('print-mode');

            if (parents[0])
            {
                parents[0].classList.remove('bill-print-parent');
            }

            originalStyles.forEach(({ element, display, visibility }) => {
                element.style.display = display;
                element.style.visibility = visibility;
            });
            billElement.style.display = originalBillDisplay;
            window.removeEventListener('afterprint', restore);
        };

        // Add listener
        window.addEventListener('afterprint', restore);

        // Wait for DOM to be fully updated and painted
        const waitAndPrint = () => {

            // Check if bill is rendered with content
            const checkBillReady = () => {
                const rect = billElement.getBoundingClientRect();
                const hasSize = rect.height > 0 && rect.width > 0;
                const hasContent = billElement.innerHTML?.length > 100;

                return hasSize && hasContent;
            };

            // Use multiple requestAnimationFrame + longer timeout
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // Wait 1 full second to ensure everything is rendered
                        setTimeout(() => {

                            if (checkBillReady())
                            {
                                console.log('✓ Bill is ready!');
                            }
                            else
                            {
                                console.warn('⚠ Bill might not be fully ready, but printing anyway...');
                            }

                            try
                            {
                                window.print();
                            }
                            catch (error)
                            {
                                restore();

                                notification.error({
                                    message: 'Lỗi in',
                                    description: 'Không thể in: ' + error.message,
                                    duration: 3
                                });
                            }

                        }, 1000); // Increased to 1 second
                    });
                });
            });
        };

        waitAndPrint();
    }, [lastCreatedOrder, activeTab]);

    // Desktop print handler using react-to-print
    const handleDesktopPrint = useReactToPrint({
        contentRef: billPrintRef,
        documentTitle: `Bill_${activeTab?.code || 'HD001'}`,
        pageStyle: `
            @page {
                size: 80mm auto;
                margin: 0;
            }
            @media print {
                body {
                    margin: 0;
                    padding: 0;
                }
            }
        `
    });

    // Mobile print handler using window.print
    const handleMobilePrint = useCallback(() =>
    {
        if (!billPrintRef.current)
        {
            notification.error({
                message: 'Lỗi in',
                description: 'Không tìm thấy hóa đơn để in.',
                duration: 3
            });
            return;
        }

        // Get the parent element that has inline style display:none
        const parentElement = billPrintRef.current.parentElement;

        if (!parentElement)
        {
            return;
        }

        // Save original inline style
        const originalStyle = parentElement.getAttribute('style');

        // Remove the display:none style by setting display to block
        parentElement.style.display = 'block';
        parentElement.style.visibility = 'visible';

        // Function to restore display after print
        const restoreDisplay = () =>
        {
            // Restore original style
            if (originalStyle)
            {
                parentElement.setAttribute('style', originalStyle);
            }
            else
            {
                parentElement.style.display = 'none';
            }
            // Remove event listeners
            window.removeEventListener('afterprint', restoreDisplay);
            window.removeEventListener('focus', handleFocusAfterPrint);
        };

        // Backup: restore on focus (for browsers that don't support afterprint)
        const handleFocusAfterPrint = () =>
        {
            setTimeout(restoreDisplay, 500);
        };

        // Listen for after print event to restore display
        window.addEventListener('afterprint', restoreDisplay);

        // For Safari and browsers without afterprint support
        window.addEventListener('focus', handleFocusAfterPrint, { once: true });

        // Use setTimeout instead of requestAnimationFrame for better compatibility
        setTimeout(() => {
            try
            {
                // Check if print is available
                if (typeof window.print === 'function')
                {
                    window.print();
                }
                else
                {
                    restoreDisplay();

                    notification.warning({
                        message: 'Không thể in',
                        description: 'Trình duyệt không hỗ trợ in. Vui lòng sử dụng menu trình duyệt.',
                        duration: 5
                    });
                }

            }
            catch (error)
            {
                restoreDisplay();

                notification.error({
                    message: 'Lỗi in',
                    description: 'Không thể mở cửa sổ in: ' + error.message,
                    duration: 5
                });
            }
        }, 300);

    }, []);

    // Main print handler that detects device type
    const handlePrint = useCallback(() =>
    {
        handleSimplePrint();
    }, [handleSimplePrint, lastCreatedOrder]);

    const handleClearCart = () => {
        dispatch(posActions.clearTab(activeTabId));
        setDiscount(0);
        setPaid(0);
        // Không xóa lastCreatedOrder ở đây để vẫn có thể in lại hóa đơn
    };

    const toggleFullscreen = () =>
    {
        if (!document.fullscreenElement)
        {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        }
        else
        {
            if (document.exitFullscreen)
            {
                document.exitFullscreen();
                setIsFullscreen(false);
            }
        }
    };

    const handleProductSearch = (value) =>
    {
        setProductSearch(value);
    };

    const handleSearchFocus = () => {
        // Show products panel when search is focused on mobile
        setShowProducts(true);
    };

    const handleSearchBlur = (e) => {
        // On mobile, if user clicks outside search and not on products section, close products
        // Use a small delay to allow click events on products to fire first
        setTimeout(() => {
            const activeEl = document.activeElement;
            const productsSection = document.querySelector('.pos-products-section');
            const searchInput = document.getElementById('pos-search-input');
            // If focus didn't move to products section or search input, close
            if (productsSection && !productsSection.contains(activeEl) && activeEl !== searchInput) {
                // Don't auto-close here to avoid interfering with product clicks
            }
        }, 200);
    };

    const handleProductsClose = () => {
        // Hide products panel on mobile
        setShowProducts(false);
        // Blur the search input to prevent onFocus from re-triggering
        document.getElementById('pos-search-input')?.blur();
    };

    const handleBarcodeToggle = () =>
    {
        setBarcodeMode(!barcodeMode);
        // Auto focus vào search input khi bật barcode mode
        if (!barcodeMode)
        {
            setTimeout(() => {
                document.getElementById('pos-search-input')?.focus();
            }, 100);
        }
    };

    // Helper function: Calculate item subtotal based on unit
    const calculateItemSubtotal = (item) => {
        if (item.unit === 'kg')
        {
            return item.quantity * 10 * item.price;
        }
        // Các unit khác: subtotal = quantity * price
        return item.price * item.quantity;
    };

    // Calculate totals
    const calculateTotals = () =>
    {
        if (!activeTab || !activeTab.items.length)
        {
            return {
                subtotal: 0,
                totalVat: 0,
                discountAmount: 0,
                shipping: 0,
                total: 0,
                change: 0
            };
        }

        const subtotal = activeTab.items.reduce((sum, item) => {
            return sum + calculateItemSubtotal(item);
        }, 0);

        const totalVat = activeTab.items.reduce((sum, item) => {
            const itemSubtotal = calculateItemSubtotal(item);
            return sum + (itemSubtotal * (item.vat || 0) / 100);
        }, 0);

        const totalBeforeDiscount = subtotal + totalVat + shipping;

        const discountAmount = discountType === 'percent'
            ? (totalBeforeDiscount * discount / 100)
            : discount;

        const total = totalBeforeDiscount - discountAmount;

        const change = paid - total;

        return {
            subtotal,
            totalVat,
            shipping,
            discountAmount,
            total,
            change: change > 0 ? change : 0
        };
    };

    const totals = calculateTotals();

    return (
        <div className="pos-container">
            {/* Header Bar */}
            <div className="pos-header">
                <div className="pos-header-search">
                    <Button background white to={'/'}><FontAwesomeIcon icon="fa-thin fa-arrow-left-long" /></Button>
                    <div className="pos-search">
                        <Input
                            id="pos-search-input"
                            className="pos-search-input"
                            placeholder={localProductsReady ? "Tìm kiếm theo tên sản phẩm, mã SKU hoặc mã vạch..." : "Đang tải dữ liệu sản phẩm..."}
                            value={productSearch}
                            onChange={(e) => handleProductSearch(e.target.value)}
                            onFocus={handleSearchFocus}
                            size="large"
                            autoFocus={barcodeMode}
                            disabled={!localProductsReady}
                        />
                        {!localProductsReady && (
                            <div className="pos-search-loading">
                                <Spin /> Đang tải dữ liệu...
                            </div>
                        )}
                    </div>
                    <button className={`barcode-btn ${barcodeMode ? 'active' : ''}`} onClick={handleBarcodeToggle} title="Quét mã vạch">
                        <BarcodeOutlined />
                        {barcodeMode && <span className="barcode-indicator">Scanning...</span>}
                    </button>
                </div>
                <div className="pos-tabs-wrapper">
                    <Tabs
                        type="editable-card"
                        activeKey={activeTabId.toString()}
                        onChange={handleTabChange}
                        addIcon={Icon.plusCircle}
                        onEdit={(targetKey, action) => {
                            if (action === 'add') {
                                handleAddTab();
                            } else {
                                handleRemoveTab(parseInt(targetKey));
                            }
                        }}
                        className="pos-tabs"
                    >
                        {tabs.map(tab => (
                            <TabPane
                                tab={tab.name}
                                key={tab.id.toString()}
                                closable={tabs.length > 1}
                            />
                        ))}
                    </Tabs>
                </div>
                <div className="pos-header-actions d-flex">
                    <SyncStatus />
                    <button className="fullscreen-btn" onClick={toggleFullscreen} title="Toàn màn hình">
                        {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                    </button>
                    {currentUser && (
                        <div className="pos-account">
                            <div className="pos-account-avatar">
                                {currentUser.avatar ? (
                                    <Image src={currentUser.avatar} alt={currentUser.firstname} />
                                ) : (
                                    <UserOutlined />
                                )}
                            </div>
                            <div className="pos-account-info">
                                <span className="pos-account-name">
                                    {currentUser.lastname}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="pos-content">
                {/* Products Section */}
                <div className={`pos-products-section ${showProducts ? 'show-mobile' : ''}`}>
                    {/* Mobile Backdrop */}
                    <div className="pos-products-backdrop" onClick={handleProductsClose}></div>

                    {/* Mobile Close Header */}
                    <div className="pos-products-header-mobile">
                        <h3>Danh sách sản phẩm</h3>
                        <button className="pos-products-close-btn" onClick={handleProductsClose}>
                            <FontAwesomeIcon icon="fa-solid fa-xmark" />
                        </button>
                    </div>

                    <div className="pos-products-grid">
                        {/* Mobile floating close button */}
                        <div className="pos-products-close-floating" onClick={handleProductsClose}>
                            <FontAwesomeIcon icon="fa-solid fa-xmark" /> Đóng
                        </div>
                        {productsLoading && <Spin /> }
                        {!productsLoading && products.length === 0 && (
                            <div className="empty-wrapper">
                                <Empty description="Không có sản phẩm nào" />
                            </div>
                        )}
                        {!productsLoading && products.map(product => (
                            <ProductItem
                                key={product.id}
                                product={product}
                                appData={appData}
                                onAdd={handleAddProduct}
                            />
                        ))}
                    </div>
                </div>

                {/* Cart Section */}
                <div className="pos-cart-section">
                    {/* Mobile Tabs */}
                    <div className="pos-mobile-tabs">
                        <button
                            className={`pos-mobile-tab ${mobileCartTab === 'items' ? 'active' : ''}`}
                            onClick={() => setMobileCartTab('items')}
                        >Giỏ hàng ({activeTab?.items?.length || 0})</button>
                        <button
                            className={`pos-mobile-tab ${mobileCartTab === 'checkout' ? 'active' : ''}`}
                            onClick={() => setMobileCartTab('checkout')}
                        >Thanh toán</button>
                    </div>

                    <div className="pos-cart-content">
                        {/* Left Column - Cart Items */}
                        <div className={`pos-cart-items-column ${mobileCartTab === 'items' ? 'show-mobile' : ''}`}>
                            <div className="pos-cart-items">
                                {(!activeTab || activeTab.items.length === 0) && (
                                    <Empty description="Chưa có sản phẩm nào" />
                                )}
                                {activeTab && activeTab.items.map(item => (
                                    <CartItem
                                        key={item.id}
                                        item={item}
                                        onIncrease={handleIncreaseQuantity}
                                        onDecrease={handleDecreaseQuantity}
                                        onRemove={handleRemoveItem}
                                        onQuantityChange={handleQuantityChange}
                                        onPriceChange={handlePriceChange}
                                        units={appData['units'] || []}
                                    />
                                ))}
                            </div>
                            {/* Mobile Checkout Button */}
                            {activeTab && activeTab.items.length > 0 && (
                                <div className="pos-mobile-checkout-btn">
                                    <Button
                                        background
                                        primary
                                        onClick={() => setMobileCartTab('checkout')}
                                        leftIcon={Icon.success}
                                    >Thanh toán</Button>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Customer Info & Summary */}
                        <div className={`pos-checkout-sidebar ${mobileCartTab === 'checkout' ? 'show-mobile' : ''}`}>
                            {/* Customer Info */}
                            <div className="pos-checkout-section">
                                {!activeTab?.customer && (
                                    <div className="pos-customer-search-wrapper">
                                        <div className="pos-customer-search">
                                            <Input
                                                placeholder="Tìm khách hàng (SĐT/Tên)"
                                                value={customerSearch}
                                                onChange={(e) => setCustomerSearch(e.target.value)}
                                                prefix={<SearchOutlined />}
                                                className="customer-search-input"
                                            />
                                            <Button
                                                background
                                                green
                                                className="add-customer-btn"
                                                onClick={handleOpenAddCustomer}
                                                title="Thêm khách hàng mới"
                                            >
                                                {Icon.plus}
                                            </Button>
                                        </div>

                                        {/* Customer search dropdown */}
                                        {showCustomerDropdown && (
                                            <div className="customer-search-dropdown">
                                                {customersLoading && (
                                                    <div className="customer-search-loading">
                                                        <Spin /> Đang tìm...
                                                    </div>
                                                )}

                                                {!customersLoading && customers.length === 0 && (
                                                    <div className="customer-search-empty">
                                                        Không tìm thấy khách hàng
                                                    </div>
                                                )}

                                                {!customersLoading && customers.length > 0 && (
                                                    <div className="customer-search-results">
                                                        {customers.map(customer => (
                                                            <div
                                                                key={customer.id}
                                                                className="customer-search-item"
                                                                onClick={() => handleSelectCustomer(customer)}
                                                            >
                                                                <div className="customer-item-icon">
                                                                    <UserOutlined />
                                                                </div>
                                                                <div className="customer-item-info">
                                                                    <div className="customer-item-name">{customer.name}</div>
                                                                    <div className="customer-item-phone">{customer.phone}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab?.customer && (
                                    <div className="pos-customer-info">
                                        <div className="pos-customer-info-data">
                                            <div className="avatar">
                                                {Icon.user}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold mb-1">{activeTab.customer.name}</p>
                                                <p className="text-xs text-primary font-medium flex items-center gap-1">
                                                    {activeTab.customer.phone}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="pos-customer-info-action">
                                            <Button
                                                className="clear-customer-btn"
                                                onClick={handleClearCustomer}
                                                title="Xóa khách hàng"
                                            >
                                                {Icon.close}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Payment Summary */}
                            <div className="pos-summary">
                                <div className="summary-row">
                                    <span>Tổng tiền hàng:</span>
                                    <strong className="money">{formatCurrency(totals.subtotal)}</strong>
                                </div>
                                <div className="summary-row">
                                    <span>Tổng VAT:</span>
                                    <strong className="money">{formatCurrency(totals.totalVat)}</strong>
                                </div>
                                <div className="summary-row shipping-row">
                                    <span>Phí vận chuyển:</span>
                                    <div className="shipping-controls">
                                        <input
                                            type="text"
                                            value={shippingDisplay}
                                            onChange={(e) => handleShippingChange(e.target.value || 0)}
                                            onFocus={(e) => e.target.select()}
                                            className="shipping-input"
                                        />
                                    </div>
                                </div>
                                <div className="summary-row discount-row">
                                    <span>Giảm giá:</span>
                                    <div className="discount-controls">
                                        <input
                                            type="number"
                                            value={discount}
                                            onChange={(e) => handleDiscountChange(parseFloat(e.target.value) || 0)}
                                            className="discount-input"
                                        />
                                        <select
                                            value={discountType}
                                            onChange={(e) => handleDiscountTypeChange(e.target.value)}
                                            className="discount-type"
                                        >
                                            <option value="amount">VNĐ</option>
                                            <option value="percent">%</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="summary-row total-row">
                                    <strong>Khách cần trả:</strong>
                                    <strong className="total-amount money">{formatCurrency(totals.total)}</strong>
                                </div>
                                {totals.total > 50000000 && (
                                    <div className="pos-high-value-warning">
                                        <span><strong>{numberToWords(totals.total)} đồng</strong>. <br />Kiểm tra kỹ trước khi thanh toán!</span>
                                    </div>
                                )}
                                <div className="summary-row paid-row">
                                    <strong>Khách đã trả:</strong>
                                    <input
                                        type="text"
                                        value={paidDisplay}
                                        onChange={(e) => handlePaidChange(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        className="paid-input"
                                        placeholder="0"
                                    />
                                </div>

                                {/* Payment Method Selection */}
                                <div className="summary-row payment-method-row">
                                    <span>Hình thức thanh toán:</span>
                                    <div className="payment-method-controls">
                                        <label className="payment-method-option">
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="cash"
                                                checked={activeTab?.paymentMethod === 'cash'}
                                                onChange={(e) => handlePaymentMethodChange(e.target.value)}
                                            />
                                            <span>Tiền mặt</span>
                                        </label>
                                        <label className="payment-method-option">
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="transfer"
                                                checked={activeTab?.paymentMethod === 'transfer'}
                                                onChange={(e) => handlePaymentMethodChange(e.target.value)}
                                            />
                                            <span>Chuyển khoản</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Suggest Money - Only show for cash payment */}
                                {activeTab?.paymentMethod === 'cash' && (
                                    <SuggestMoney
                                        requiredAmount={totals.total}
                                        onSelect={handlePaidChange}
                                    />
                                )}

                                {/* QR Code - Only show for transfer payment */}
                                {activeTab?.paymentMethod === 'transfer' && (
                                    <div className="qr-code-section">
                                        {qrLoading && <Loading noFixed />}
                                        {!qrLoading && activeTab?.qrCodeUrl && (
                                            <div className="qr-code-wrapper">
                                                <img src={activeTab.qrCodeUrl} alt="QR Code" className="qr-code-image" />
                                                <p className="qr-code-hint">Quét mã để thanh toán</p>
                                            </div>
                                        )}
                                        {!qrLoading && !activeTab?.qrCodeUrl && (
                                            <div className="qr-code-error">
                                                <p>Không thể tải mã QR. Vui lòng thử lại.</p>
                                                <Button small background blue onClick={loadQrCode}>Tải lại</Button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="summary-row change-row">
                                    <span>Tiền thừa trả khách:</span>
                                    <strong className="change-amount money">{formatCurrency(totals.change)}</strong>
                                </div>
                            </div>

                            <div className="pos-actions">
                                <Button outline red onClick={handleClearCart} leftIcon={Icon.delete}>
                                    Xóa
                                </Button>
                                {lastCreatedOrder && (
                                    <Button
                                        background
                                        blue
                                        onClick={handlePrint}
                                        leftIcon={Icon.print}
                                    >
                                        In hóa đơn
                                    </Button>
                                )}
                                <Button
                                    background
                                    primary
                                    onClick={handleCheckout}
                                    leftIcon={Icon.success}
                                    disabled={!activeTab || activeTab.items.length === 0 || checkoutLoading}
                                >
                                    {checkoutLoading ? 'Đang xử lý...' : 'Thanh toán'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bill Print Component - Hidden */}
            <div id="bill-print-wrapper" style={{display: 'none'}}>
                <BillPrint
                    ref={billPrintRef}
                    billData={{
                        code: orderDataRef.current?.code || lastCreatedOrder?.code || activeTab?.code || 'HD001',
                        date: orderDataRef.current?.created || lastCreatedOrder?.created || new Date().toLocaleString('vi-VN'),
                        cashier: orderDataRef.current?.user_created_name || lastCreatedOrder?.user_created_name || currentUser?.username || 'Admin',
                        customer: orderDataRef.current?.customer_name || lastCreatedOrder?.customer_name || activeTab?.customer?.name || '',
                        address: orderDataRef.current?.customer_address || lastCreatedOrder?.customer_address || activeTab?.customer?.address || '',
                        phone: orderDataRef.current?.customer_phone || lastCreatedOrder?.customer_phone || activeTab?.customer?.phone || '',
                        items: orderDataRef.current?.items || lastCreatedOrder?.items || activeTab?.items || [],
                        subtotal: orderDataRef.current?.subtotal || lastCreatedOrder?.subtotal || totals.subtotal,
                        totalVat: orderDataRef.current?.vat || lastCreatedOrder?.vat || totals.totalVat,
                        discountAmount: orderDataRef.current?.discount || lastCreatedOrder?.discount || totals.discountAmount,
                        discountType: orderDataRef.current?.discount_type || lastCreatedOrder?.discount_type || discountType,
                        discount: orderDataRef.current?.discount_value || lastCreatedOrder?.discount_value || discount,
                        shipping: orderDataRef.current?.shipping || lastCreatedOrder?.shipping || shipping,
                        total: orderDataRef.current?.total || lastCreatedOrder?.total || totals.total,
                        paid: orderDataRef.current?.paid || lastCreatedOrder?.paid || paid,
                        change: orderDataRef.current?.change || lastCreatedOrder?.change || totals.change
                    }}
                />
            </div>

            {/* Add Customer Modal */}
            <AddCustomerModal
                open={showAddCustomerModal}
                onClose={handleCloseAddCustomer}
                onSuccess={handleAddCustomerSuccess}
            />
        </div>
    );
};

export default Pos;
